import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBag } from '../context/BagContext';
import { usePayment } from '../context/PaymentContext';
import { useAddress } from '../context/AddressContext';
import { useOrders } from '../context/OrderContext';
import { cartService, orderService, settingsService } from '../services/api';
import {
  buildWhatsAppUrl,
  normalizeWhatsappDigits,
  getWhatsAppOrderPhoneDigits,
  CONTACT_CHANNELS_UPDATED_EVENT,
  parseContactChannelsFromStorage,
  normalizeContactChannelsData,
  computeShippingFromSubtotal,
} from '../utils/whatsappContact';
import { getApiBaseUrl } from '../config/environment';
import { showCheckoutSuccess } from '../components/CheckoutSuccess';
import SiteLoading from '../components/SiteLoading';
import { PLACEHOLDER_NO_IMAGE_SMALL } from '../utils/placeholderImage';
import { getSavedCouponCodes } from '../utils/savedCouponsStorage';
import {
  checkoutExplicitCouponMatchesCart,
  setCheckoutExplicitCoupon,
  clearCheckoutExplicitCoupon,
} from '../utils/checkoutExplicitCouponSession';
import { mergeGuestCartToApi, guestBagReactSnapshotRef } from '../utils/mergeGuestCartToApi';
import {
  inferCanonicalFromStorefront,
  normalizeCheckoutPaymentMethodsFromApi,
  canonicalMatchesStorefrontPayment,
} from '../utils/checkoutPaymentMethod';
import './CheckoutPage.css';

/** Full checkout summary for WhatsApp (plain text; * for bold in WA). */
function buildCheckoutWhatsAppOrderMessage({
  user,
  cart,
  selectedAddress,
  paymentMethodLabel,
  subtotal,
  saleDiscount,
  couponDiscount,
  shipping,
  total,
  deliveryInfo,
}) {
  const lines = [];
  lines.push('🛍️ *ORDER VIA WHATSAPP (CHECKOUT)* 🛍️');
  lines.push('');
  if (user?.name || user?.email || user?.phone) {
    lines.push('*Customer*');
    if (user.name) lines.push(`Name: ${user.name}`);
    if (user.email) lines.push(`Email: ${user.email}`);
    if (user.phone) lines.push(`Phone: ${user.phone}`);
    lines.push('');
  }
  if (selectedAddress) {
    lines.push('*Delivery address*');
    const a = selectedAddress;
    const line1 = a.addressLine1 || a.address || '';
    if (line1) lines.push(line1);
    if (a.addressLine2) lines.push(a.addressLine2);
    const cityParts = [a.city, a.state, a.postalCode].filter(Boolean);
    if (cityParts.length) lines.push(cityParts.join(', '));
    if (a.country) lines.push(String(a.country));
    if (a.phone) lines.push(`Address phone: ${a.phone}`);
    lines.push('');
  } else {
    lines.push('*Delivery address:* (not selected on site)');
    lines.push('');
  }

  lines.push('*Items*');
  (cart?.items || []).forEach((item, i) => {
    const name = item.product?.name || 'Product';
    const qty = item.quantity || 1;
    const price = Number(item.price) || 0;
    const lineTotal = price * qty;
    const color =
      item.color && String(item.color).toLowerCase() !== 'default'
        ? item.color
        : null;
    const size =
      item.size && String(item.size).toLowerCase() !== 'default'
        ? item.size
        : null;
    lines.push(`${i + 1}) ${name}`);
    lines.push(
      `   Qty: ${qty} × PKR ${price.toLocaleString()} = PKR ${lineTotal.toLocaleString()}`
    );
    const specs = [color ? `Colour: ${color}` : '', size ? `Size: ${size}` : '']
      .filter(Boolean)
      .join(' | ');
    if (specs) lines.push(`   ${specs}`);
    const sku = item.product?.sku || item.sku;
    if (sku) lines.push(`   SKU: ${sku}`);
  });
  lines.push('');

  lines.push('*Summary*');
  lines.push(`Items subtotal: PKR ${Number(subtotal).toLocaleString()}`);
  if (saleDiscount > 0) {
    lines.push(`Sale discount: -PKR ${Number(saleDiscount).toLocaleString()}`);
  }
  if (couponDiscount > 0) {
    const code = cart?.coupon?.code;
    lines.push(
      code
        ? `Coupon (${code}): -PKR ${Number(couponDiscount).toLocaleString()}`
        : `Coupon discount: -PKR ${Number(couponDiscount).toLocaleString()}`
    );
  }
  const shipLabel = deliveryInfo?.qualified
    ? 'FREE'
    : `PKR ${Number(shipping).toLocaleString()}`;
  lines.push(`Shipping (${shipLabel})`);
  lines.push(`*TOTAL: PKR ${Number(total).toLocaleString()}*`);
  lines.push('');
  lines.push(
    `*Payment method:* ${paymentMethodLabel || 'Not selected yet'}`
  );
  lines.push('');
  lines.push(
    'Please confirm this order on your side. I placed it from the website checkout.'
  );
  if (typeof window !== 'undefined' && window.location?.href) {
    lines.push('');
    lines.push(`Page: ${window.location.href}`);
  }
  return lines.join('\n');
}

const CheckoutPage = () => {
  const navigate = useNavigate();
  /** Guests complete account + delivery on Bag; checkout is authenticated-only. */
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { clearBag: clearGuestBag } = useBag();
  const {
    getActivePaymentMethods,
    paymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    loading: paymentMethodsLoading,
  } = usePayment();
  const { getUserAddresses, getDefaultAddress } = useAddress();
  const { addOrder } = useOrders();

  // State
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userAddresses, setUserAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponNotice, setCouponNotice] = useState(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [error, setError] = useState(null);
  const [cartSummary, setCartSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  
  // State for expanded payment method details
  const [expandedPaymentMethod, setExpandedPaymentMethod] = useState(null);
  const [paymentTransferConfirmed, setPaymentTransferConfirmed] = useState(false);
  const [copiedPaymentMethodId, setCopiedPaymentMethodId] = useState(null);

  /** Canonical order.paymentMethod (cod | card | bank_transfer | digital_wallet) from GET checkout/payment-methods */
  const [checkoutCanonicalMethods, setCheckoutCanonicalMethods] = useState([]);
  const [checkoutCanonicalLoading, setCheckoutCanonicalLoading] = useState(true);
  const [checkoutCanonicalApiMessage, setCheckoutCanonicalApiMessage] = useState('');
  const [selectedCanonicalPaymentMethod, setSelectedCanonicalPaymentMethod] = useState('');

  const activeStorefrontPaymentMethods = React.useMemo(
    () => (paymentMethods || []).filter((m) => m?.isActive && m?.isVisible),
    [paymentMethods]
  );

  /** Backend slugs that also have a matching configured payment account */
  const checkoutCanonicalMethodsVisible = React.useMemo(() => {
    if (!Array.isArray(checkoutCanonicalMethods) || checkoutCanonicalMethods.length === 0) {
      return [];
    }
    return checkoutCanonicalMethods.filter((row) =>
      activeStorefrontPaymentMethods.some((m) =>
        canonicalMatchesStorefrontPayment(row.value, m.type)
      )
    );
  }, [checkoutCanonicalMethods, activeStorefrontPaymentMethods]);

  /** Only accounts for the payment type the shopper selected */
  const storefrontMethodsForSelectedCanonical = React.useMemo(() => {
    if (!selectedCanonicalPaymentMethod) return [];
    return activeStorefrontPaymentMethods.filter((m) =>
      canonicalMatchesStorefrontPayment(selectedCanonicalPaymentMethod, m.type)
    );
  }, [activeStorefrontPaymentMethods, selectedCanonicalPaymentMethod]);

  const paymentMethodHasDetails = React.useCallback((method) => {
    if (!method) return false;
    const hasAccountNumber =
      method.accountNumber && String(method.accountNumber).trim() !== '';
    return !!(
      hasAccountNumber ||
      method.accountTitle ||
      method.bankName ||
      method.description
    );
  }, []);

  const copyPaymentAccountNumber = React.useCallback(async (rawText, methodId) => {
    const text = String(rawText || '')
      .replace(/\u00a0/g, ' ')
      .trim();
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error('clipboard unavailable');
      }
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.top = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, text.length);
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        return;
      }
    }
    setCopiedPaymentMethodId(methodId);
    window.setTimeout(() => {
      setCopiedPaymentMethodId((prev) => (prev === methodId ? null : prev));
    }, 2000);
  }, []);
  const getReadableErrorMessage = React.useCallback((value, fallback = '') => {
    if (!value) return fallback;
    let msg = typeof value === 'string' ? value : String(value);
    try {
      const parsed = JSON.parse(msg);
      msg = parsed?.error || parsed?.message || msg;
    } catch {
      // Keep original if it is not a JSON string
    }
    return String(msg);
  }, []);

  const displayError = React.useMemo(
    () => getReadableErrorMessage(error || '', ''),
    [error, getReadableErrorMessage]
  );
  const isExpiredCouponError = React.useMemo(
    () => displayError.toLowerCase().includes('expired'),
    [displayError]
  );


  const [waContact, setWaContact] = useState(() =>
    normalizeContactChannelsData(parseContactChannelsFromStorage() || {})
  );

  const paymentSectionRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/bag', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    setPaymentTransferConfirmed(false);
  }, [selectedPaymentMethod, selectedCanonicalPaymentMethod]);

  /** Load canonical payment options (public endpoint; aligns with POST /orders validator). */
  useEffect(() => {
    if (!isAuthenticated) {
      setCheckoutCanonicalLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setCheckoutCanonicalLoading(true);
      const res = await orderService.getCheckoutPaymentMethods();
      if (cancelled) return;
      const rawMethods = res?.data?.methods ?? [];
      const normalized = normalizeCheckoutPaymentMethodsFromApi(rawMethods);
      setCheckoutCanonicalMethods(normalized);
      setCheckoutCanonicalApiMessage(
        typeof res?.data?.message === 'string' ? res.data.message : ''
      );
      setCheckoutCanonicalLoading(false);
      if (normalized.length > 0) {
        setSelectedCanonicalPaymentMethod((prev) => {
          if (prev && normalized.some((m) => m.value === prev)) return prev;
          const codRow = normalized.find((m) => m.value === 'cod');
          return codRow ? codRow.value : normalized[0].value;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  /** If the selected canonical type is not in the API ∩ accounts list, pick a valid one */
  useEffect(() => {
    const visible = checkoutCanonicalMethodsVisible;
    if (!visible.length) return;
    if (!visible.some((m) => m.value === selectedCanonicalPaymentMethod)) {
      const codRow = visible.find((m) => m.value === 'cod');
      setSelectedCanonicalPaymentMethod(codRow ? codRow.value : visible[0].value);
    }
  }, [checkoutCanonicalMethodsVisible, selectedCanonicalPaymentMethod]);

  /** Keep storefront account selection aligned with canonical type (only matching accounts are shown). */
  useEffect(() => {
    if (!selectedCanonicalPaymentMethod || paymentMethodsLoading) return;
    const matches = activeStorefrontPaymentMethods.filter((m) =>
      canonicalMatchesStorefrontPayment(selectedCanonicalPaymentMethod, m.type)
    );
    if (!matches.length) return;
    const currentOk =
      selectedPaymentMethod && matches.some((m) => m.type === selectedPaymentMethod);
    if (currentOk) return;
    const pick = matches[0];
    setSelectedPaymentMethod(pick.type);
    if (paymentMethodHasDetails(pick)) {
      setExpandedPaymentMethod(pick.id);
    } else {
      setExpandedPaymentMethod(null);
    }
  }, [
    selectedCanonicalPaymentMethod,
    paymentMethodsLoading,
    activeStorefrontPaymentMethods,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    paymentMethodHasDetails,
  ]);

  useEffect(() => {
    if (!couponNotice?.text) return undefined;
    const timer = window.setTimeout(() => {
      setCouponNotice(null);
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [couponNotice]);

  useEffect(() => {
    let cancelled = false;
    settingsService.getContactChannels().then((res) => {
      if (cancelled) return;
      const d = res?.data || res;
      if (d && typeof d === 'object') {
        setWaContact(normalizeContactChannelsData(d));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncFromStorage = () => {
      const ch = parseContactChannelsFromStorage();
      if (!ch) return;
      setWaContact(normalizeContactChannelsData(ch));
    };
    window.addEventListener(CONTACT_CHANNELS_UPDATED_EVENT, syncFromStorage);
    window.addEventListener('storage', syncFromStorage);
    return () => {
      window.removeEventListener(CONTACT_CHANNELS_UPDATED_EVENT, syncFromStorage);
      window.removeEventListener('storage', syncFromStorage);
    };
  }, []);

  // Fetch cart with retry logic for version conflicts
  // Use ref to prevent duplicate fetches
  const fetchingCartRef = useRef(false);
  
  useEffect(() => {
    if (!isAuthenticated) {
      fetchingCartRef.current = false;
      return;
    }

    // Prevent duplicate fetches
    if (fetchingCartRef.current) {
      return;
    }

    const fetchCart = async (retryCount = 0) => {
      fetchingCartRef.current = true;
      try {
        setLoading(true);
        await mergeGuestCartToApi(clearGuestBag, () => guestBagReactSnapshotRef.current);

        const response = await cartService.getCart();

        // Handle version conflict errors
        if (!response.success && response.error) {
          const errorMessage = response.error || response.message || '';
          const isVersionConflict = errorMessage.includes('No matching document found') || 
                                    errorMessage.includes('version') ||
                                    errorMessage.includes('modifiedPaths');
          
          if (isVersionConflict && retryCount < 2) {
            // Retry with delay
            await new Promise(resolve => setTimeout(resolve, 300 * (retryCount + 1)));
            return fetchCart(retryCount + 1);
          }
        }

        if (response?.data) {
          setCart(response.data);
          setError(null);
          if (!response.success && typeof window !== 'undefined' && window.showToast) {
            window.showToast(
              'Cart could not be fully loaded from the server. If checkout looks empty, open Bag and try again.',
              'warning',
              6000
            );
          }
        } else if (!response.success) {
          setError('Failed to load cart');
        }
      } catch (err) {
        console.warn('Checkout cart fetch:', err?.message || err);
        // Check if it's a version conflict
        const errorMessage = err?.message || err?.error || '';
        const isVersionConflict = errorMessage.includes('No matching document found') || 
                                  errorMessage.includes('version') ||
                                  errorMessage.includes('modifiedPaths');
        
        if (!isVersionConflict) {
          setError('Failed to load cart');
        } else if (retryCount < 2) {
          // Retry for version conflicts
          await new Promise(resolve => setTimeout(resolve, 300 * (retryCount + 1)));
          return fetchCart(retryCount + 1);
        }
      } finally {
        setLoading(false);
        fetchingCartRef.current = false;
      }
    };

    fetchCart();
  }, [isAuthenticated, clearGuestBag]);

  const userId = user?.id ?? user?._id;
  const [savedCouponCodes, setSavedCouponCodes] = useState([]);

  useEffect(() => {
    const syncSaved = () => {
      setSavedCouponCodes(userId ? getSavedCouponCodes(userId) : []);
    };
    syncSaved();
    window.addEventListener('sheenwearSavedCouponsUpdated', syncSaved);
    return () => window.removeEventListener('sheenwearSavedCouponsUpdated', syncSaved);
  }, [userId]);

  /** Bumps when user explicitly applies a coupon so in-flight "strip" does not overwrite the cart. */
  const couponStripGenRef = useRef(0);

  /** Drop server-persisted cart coupons on checkout unless shopper applied them this session. */
  useEffect(() => {
    if (!isAuthenticated || !cart?._id) return;
    const couponCodeRaw = cart?.coupon?.code || cart?.coupon?.couponCode;
    if (!cart?.coupon || !String(couponCodeRaw || '').trim()) return;
    if (checkoutExplicitCouponMatchesCart(cart)) return;

    const genAtStart = couponStripGenRef.current;
    let cancelled = false;
    (async () => {
      try {
        const response = await cartService.removeCoupon(cart._id);
        if (cancelled || genAtStart !== couponStripGenRef.current) return;
        if (response?.success && response?.data) {
          setCart(response.data);
        } else {
          const r = await cartService.getCart();
          if (!cancelled && genAtStart === couponStripGenRef.current && r.success && r.data) {
            setCart(r.data);
          }
        }
      } catch (e) {
        console.warn('Checkout: could not clear unprompted cart coupon', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, cart?._id, cart?.coupon?.code, cart?.coupon?.couponCode]);

  // Fetch addresses
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadAddresses = async () => {
      try {
        const addresses = await getUserAddresses();
        setUserAddresses(addresses || []);
        
        // Set default address
        const defaultAddr = getDefaultAddress(addresses || []);
        if (defaultAddr) {
          setSelectedAddress(defaultAddr);
        } else if (addresses && addresses.length > 0) {
          setSelectedAddress(addresses[0]);
        }
      } catch (err) {
        console.error('Error loading addresses:', err);
      }
    };

    loadAddresses();
  }, [isAuthenticated, getUserAddresses, getDefaultAddress]);

  // Fetch cart summary from API
  const fetchCartSummary = async () => {
    if (!isAuthenticated || !cart) {
      return;
    }

    try {
      setLoadingSummary(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        console.warn('No auth token found for cart summary');
        return;
      }

      const response = await fetch(`${getApiBaseUrl()}/cart/summary`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart summary');
      }

      const result = await response.json();
      
      if (result.data) {
        console.log('✅ Cart Summary fetched:', result.data);
        setCartSummary(result.data);
      }
    } catch (error) {
      console.error('Error fetching cart summary:', error);
      // Don't set error state, just log it - fallback to manual calculation
    } finally {
      setLoadingSummary(false);
    }
  };

  // Fetch cart summary when cart is loaded
  useEffect(() => {
    if (isAuthenticated && cart) {
      fetchCartSummary();
    }
  }, [isAuthenticated, cart?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to extract image URL
  const getImageUrl = (imageData) => {
    if (!imageData) return '';
    if (typeof imageData === 'string') return imageData;
    if (Array.isArray(imageData) && imageData.length > 0) {
      return typeof imageData[0] === 'string' ? imageData[0] : imageData[0].url || '';
    }
    if (imageData.url) return imageData.url;
    return '';
  };

  const itemSubtotal = React.useMemo(() => {
    if (!cart?.items || cart.items.length === 0) return 0;
    return cart.items.reduce((sum, item) => {
      const price = Number(item?.price) || 0;
      const quantity = Number(item?.quantity) || 1;
      return sum + (price * quantity);
    }, 0);
  }, [cart?.items]);

  // Calculate total original amount (before sale discount)
  const totalOriginalAmount = React.useMemo(() => {
    // Use API data if available
    if (cartSummary?.totalAmount !== undefined) {
      return cartSummary.totalAmount || 0;
    }
    
    // Fallback to manual calculation
    if (!cart?.items || cart.items.length === 0) return 0;
    
    return cart.items.reduce((total, item) => {
      const originalPrice = item.originalPrice || item.product?.originalPrice;
      const price = item.price;
      const itemOriginalPrice = originalPrice && originalPrice > price ? originalPrice : price;
      return total + (itemOriginalPrice * (item.quantity || 1));
    }, 0);
  }, [cart?.items, cartSummary]);

  const saleDiscount = React.useMemo(
    () => Math.max(0, totalOriginalAmount - itemSubtotal),
    [totalOriginalAmount, itemSubtotal]
  );

  // Calculate sale discount percentage
  const saleDiscountPercent = totalOriginalAmount > 0 
    ? Math.round((saleDiscount / totalOriginalAmount) * 100) 
    : 0;

  // Calculate coupon discount
  const couponDiscount = React.useMemo(() => {
    if (!cart?.coupon) return 0;
    const beforeCoupon = Number(cart?.subtotal) || 0;
    const afterCoupon = Number(cart?.total) || beforeCoupon;
    return Math.max(0, beforeCoupon - afterCoupon);
  }, [cart]);

  const couponTerms = React.useMemo(() => {
    const c = cart?.coupon;
    if (!c) return [];

    const toAmount = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.max(0, n) : 0;
    };

    const minSpend = toAmount(
      c.minOrderAmount ?? c.minimumOrderAmount ?? c.minimumSpend ?? c.minPurchaseAmount
    );
    const percentOff = toAmount(
      c.discountPercent ?? c.percentage ?? c.percent ?? c.discountPercentage
    );
    const flatOff = toAmount(
      c.discountAmount ?? c.flatDiscount ?? c.amountOff ?? c.value
    );
    const maxDiscount = toAmount(c.maxDiscountAmount ?? c.maxDiscount ?? c.capAmount);
    const freeShipMin = toAmount(
      c.freeShippingMinAmount ?? c.freeShippingThreshold ?? c.shippingThreshold
    );

    const terms = [];

    if (minSpend > 0) {
      terms.push(`Min order: PKR ${minSpend.toLocaleString()}`);
    }
    if (percentOff > 0) {
      terms.push(`${percentOff}% off`);
    } else if (flatOff > 0) {
      terms.push(`Flat PKR ${flatOff.toLocaleString()} off`);
    }
    if (maxDiscount > 0) {
      terms.push(`Max discount: PKR ${maxDiscount.toLocaleString()}`);
    }
    if (freeShipMin > 0) {
      terms.push(`Free shipping over PKR ${freeShipMin.toLocaleString()}`);
    }

    return terms.slice(0, 4);
  }, [cart?.coupon]);

  // Calculate subtotal (items only, before shipping) - no hidden charges.
  const subtotal = React.useMemo(() => {
    if (itemSubtotal > 0 || (cart?.items?.length || 0) > 0) return itemSubtotal;
    return Number(cart?.subtotal) || 0;
  }, [itemSubtotal, cart?.items?.length, cart?.subtotal]);
  
  const deliveryInfo = React.useMemo(
    () => computeShippingFromSubtotal(subtotal, waContact),
    [subtotal, waContact]
  );
  const shipping = deliveryInfo.shipping;
  
  // Total original amount (before any discounts)
  const totalOriginalBeforeDiscount = React.useMemo(() => {
    return totalOriginalAmount;
  }, [totalOriginalAmount]);
  
  // Final total (after coupon + shipping; sale discount already in item prices)
  const total = React.useMemo(() => {
    const payableBeforeShipping = Math.max(0, subtotal - couponDiscount);
    return payableBeforeShipping + shipping;
  }, [subtotal, couponDiscount, shipping]);

  const waLinks = React.useMemo(() => {
    const chatPhone = waContact.whatsappChatNumber || waContact.whatsappOrderNumber || '';
    const orderPhone = waContact.whatsappOrderNumber || waContact.whatsappChatNumber || '';
    const c = normalizeWhatsappDigits(chatPhone);
    const o = normalizeWhatsappDigits(orderPhone);
    const orderMsg = 'Hi! I would like to place an order via WhatsApp.';
    const orderUrlPrimary = buildWhatsAppUrl(orderPhone, orderMsg);
    return {
      orderUrl: orderUrlPrimary,
      showOrderBtn: Boolean(o && c && o !== c),
    };
  }, [waContact]);

  /** Place Order ke neeche — poori cart / address / payment / totals WA message men */
  const checkoutWhatsAppOrderUrl = React.useMemo(() => {
    const digits = getWhatsAppOrderPhoneDigits();
    if (!digits || !cart?.items?.length) return '';

    const methods = getActivePaymentMethods();
    const pm = methods.find((m) => m.type === selectedPaymentMethod);
    const canonLabel =
      checkoutCanonicalMethodsVisible.find((m) => m.value === selectedCanonicalPaymentMethod)
        ?.label ||
      checkoutCanonicalMethods.find((m) => m.value === selectedCanonicalPaymentMethod)?.label;
    const paymentMethodLabel =
      canonLabel || pm?.name || selectedPaymentMethod || 'Not selected yet';

    let msg = buildCheckoutWhatsAppOrderMessage({
      user,
      cart,
      selectedAddress,
      paymentMethodLabel,
      subtotal,
      saleDiscount,
      couponDiscount,
      shipping,
      total,
      deliveryInfo,
    });

    const maxRaw = 3800;
    if (msg.length > maxRaw) {
      msg =
        msg.slice(0, maxRaw - 60).trim() +
        '\n\n…(details truncated — full cart on website checkout)';
    }

    return buildWhatsAppUrl(digits, msg);
  }, [
    user,
    cart,
    selectedAddress,
    selectedPaymentMethod,
    getActivePaymentMethods,
    subtotal,
    saleDiscount,
    couponDiscount,
    shipping,
    total,
    deliveryInfo,
    checkoutCanonicalMethods,
    checkoutCanonicalMethodsVisible,
    selectedCanonicalPaymentMethod,
  ]);

  /** Bank / wallet / etc.: WhatsApp to send payment proof (per-account number or store default). */
  const { paymentProofWhatsAppUrl, paymentProofTargetDigits, paymentProofUsesAccountNumber } =
    React.useMemo(() => {
      const empty = {
        paymentProofWhatsAppUrl: '',
        paymentProofTargetDigits: '',
        paymentProofUsesAccountNumber: false,
      };
      if (
        selectedCanonicalPaymentMethod === 'cod' ||
        selectedPaymentMethod === 'cash-on-delivery'
      ) {
        return empty;
      }
      const t = String(selectedPaymentMethod).toLowerCase();
      const method = paymentMethods.find(
        (m) => String(m.type || '').toLowerCase() === t
      );
      if (!method) return empty;
      const raw = (method.paymentProofWhatsApp || '').trim();
      const proofDigits = normalizeWhatsappDigits(raw);
      const fallbackDigits = getWhatsAppOrderPhoneDigits();
      const phoneDigits = proofDigits || fallbackDigits;
      if (!phoneDigits) return empty;
      const pm = method.name || selectedPaymentMethod;
      const lines = [
        '🛍️ *Payment proof — website checkout*',
        '',
        'Hello! I have *completed the payment* for my order on your website.',
        '',
        'I am sending my *payment proof* (screenshot or receipt) in this chat.',
        '',
      ];
      if (user?.name) lines.push(`*Customer:* ${user.name}`);
      lines.push(`*Amount to pay:* PKR ${Number(total).toLocaleString()}`);
      lines.push(`*Payment method:* ${pm}`);
      lines.push('');
      lines.push('Please confirm receipt. Thank you!');
      return {
        paymentProofWhatsAppUrl: buildWhatsAppUrl(phoneDigits, lines.join('\n')),
        paymentProofTargetDigits: phoneDigits,
        paymentProofUsesAccountNumber: Boolean(proofDigits),
      };
    }, [selectedPaymentMethod, selectedCanonicalPaymentMethod, total, user?.name, paymentMethods]);

  const applyPromoCode = async (rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code || !isAuthenticated) return;

    try {
      setApplyingCoupon(true);
      setError(null);
      const response = await cartService.applyCoupon(code);

      if (response.success) {
        couponStripGenRef.current += 1;
        const cartResponse = await cartService.getCart();
        if (cartResponse.success && cartResponse.data) {
          const d = cartResponse.data;
          const appliedCode = d.coupon?.code || code;
          setCheckoutExplicitCoupon(d._id, appliedCode);
          setCart(d);
        }
        setCouponNotice({ type: 'success', text: 'Coupon applied successfully.' });
        setPromoCode('');
      } else {
        const msg = getReadableErrorMessage(response.message, 'Invalid coupon code');
        const isExpired = msg.toLowerCase().includes('expired');
        setCouponNotice({ type: isExpired ? 'expired' : 'error', text: msg });
      }
    } catch (err) {
      console.error('Error applying coupon:', err);
      const msg = getReadableErrorMessage(err?.message || err?.error, 'Failed to apply coupon');
      const isExpired = msg.toLowerCase().includes('expired');
      setCouponNotice({ type: isExpired ? 'expired' : 'error', text: msg });
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleApplyCoupon = async () => {
    await applyPromoCode(promoCode);
  };

  // Handle remove coupon
  const handleRemoveCoupon = async () => {
    clearCheckoutExplicitCoupon();
    try {
      setApplyingCoupon(true);
      const response = await cartService.removeCoupon(cart?._id);
      if (response?.success && response?.data) {
        setCart(response.data);
        setCouponNotice({
          type: 'success',
          text: response.localOnly ? 'Coupon removed.' : 'Coupon removed.',
        });
      } else {
        // Refresh cart as fallback
        const cartResponse = await cartService.getCart();
        if (cartResponse.success && cartResponse.data) {
          setCart(cartResponse.data);
        }
        setCouponNotice({ type: 'success', text: 'Coupon removed.' });
      }
    } catch (err) {
      console.error('Error removing coupon:', err);
      const msg = getReadableErrorMessage(err?.message || err?.error, 'Failed to remove coupon');
      setCouponNotice({ type: 'error', text: msg });
    } finally {
      setApplyingCoupon(false);
    }
  };

  // Handle place order
  const handlePlaceOrder = async () => {
    if (!isAuthenticated) {
      setError('Please login to place an order');
      return;
    }
    
    if (!selectedAddress) {
      setError('Please select a delivery address');
      return;
    }

    if (checkoutCanonicalLoading && checkoutCanonicalMethods.length === 0) {
      setError('Loading payment options… please wait a moment.');
      return;
    }

    let canonical = '';
    if (checkoutCanonicalMethods.length > 0) {
      if (!checkoutCanonicalMethodsVisible.length) {
        setError(
          'No payment type from the server matches your store accounts. Contact support or update payment settings.'
        );
        return;
      }
      if (
        !selectedCanonicalPaymentMethod ||
        !checkoutCanonicalMethodsVisible.some((m) => m.value === selectedCanonicalPaymentMethod)
      ) {
        setError('Please choose how you will pay (payment type).');
        return;
      }
      canonical = selectedCanonicalPaymentMethod;
    } else {
      canonical = inferCanonicalFromStorefront(selectedPaymentMethod) || '';
    }

    if (!canonical) {
      setError('Please select a payment method');
      return;
    }

    const isCodCanonical = canonical === 'cod';

    if (!isCodCanonical) {
      const storefrontOk =
        selectedPaymentMethod &&
        canonicalMatchesStorefrontPayment(canonical, selectedPaymentMethod);
      if (!storefrontOk) {
        setError(
          'Pick the payment account that matches your selected type (e.g. bank account or wallet).'
        );
        return;
      }
    }

    if (!isCodCanonical && !paymentTransferConfirmed) {
      setError(
        'Please confirm that you have completed the payment, or use WhatsApp to send your proof first.'
      );
      return;
    }

    try {
      setIsPlacingOrder(true);
      setError(null);

      // Prepare order data according to API specification
      // ✅ DO NOT include orderNumber - backend automatically generates it
      const orderData = {
        items: cart.items.map(item => ({
          product: item.product._id || item.product.id,
          quantity: item.quantity,
          color: item.color || 'default',
          size: item.size || 'default'
        })),
        paymentMethod: canonical,
        shippingAddress: {
          type: selectedAddress.type || 'home',
          addressLine1: selectedAddress.addressLine1 || selectedAddress.address || '',
          addressLine2: selectedAddress.addressLine2 || '',
          city: selectedAddress.city || '',
          state: selectedAddress.state || '',
          postalCode: selectedAddress.postalCode || '',
          country: selectedAddress.country || 'Pakistan',
          phone: selectedAddress.phone || user?.phone || ''
        },
        billingAddress: {
          type: selectedAddress.type || 'home',
          addressLine1: selectedAddress.addressLine1 || selectedAddress.address || '',
          addressLine2: selectedAddress.addressLine2 || '',
          city: selectedAddress.city || '',
          state: selectedAddress.state || '',
          postalCode: selectedAddress.postalCode || '',
          country: selectedAddress.country || 'Pakistan',
          phone: selectedAddress.phone || user?.phone || ''
        },
        notes: {
          customer: '',
          internal: ''
        }
        // ✅ orderNumber is NOT included - backend generates it automatically (format: SW2412010001)
      };

      // Create order
      const response = await orderService.createOrder(orderData);

      if (response.success) {
        const order = response.data;
        
        if (!order.orderNumber) {
          order.orderNumber = `TEMP-${order._id ? order._id.slice(-8) : 'UNKNOWN'}`;
        }

        // COD / other methods: show success modal
        const orderDetails = {
          orderId: order._id || 'N/A',
          orderNumber: order.orderNumber || 'N/A',
          orderDate: new Date(order.createdAt || Date.now()).toLocaleDateString(),
          deliveryDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString(),
          paymentMethod:
            checkoutCanonicalMethodsVisible.find((m) => m.value === canonical)?.label ||
            checkoutCanonicalMethods.find((m) => m.value === canonical)?.label ||
            selectedPaymentMethod ||
            canonical,
          paymentStatus: order.paymentStatus || 'pending',
          shippingAddress: `${selectedAddress.addressLine1 || selectedAddress.address}, ${selectedAddress.city}`,
          total: order.total || total
        };

        showCheckoutSuccess(orderDetails);

        window.dispatchEvent(new CustomEvent('orderPlaced', { detail: order }));

        try {
          await cartService.clearCart();
        } catch (clearError) {
          console.warn('Cart clear warning:', clearError);
        }
        clearCheckoutExplicitCoupon();
        setCart(null);

        setTimeout(() => {
          navigate('/orders');
        }, 2000);
      } else {
        // Handle API error responses
        const errorMessage = response.error || response.message || 'Failed to place order';
        const validationErrors = response.errors;

        if (
          Array.isArray(response.availablePaymentMethods) &&
          response.availablePaymentMethods.length > 0
        ) {
          const next = normalizeCheckoutPaymentMethodsFromApi(
            response.availablePaymentMethods
          );
          setCheckoutCanonicalMethods(next);
          setSelectedCanonicalPaymentMethod((prev) => {
            if (prev && next.some((m) => m.value === prev)) return prev;
            const c = next.find((m) => m.value === 'cod');
            return c ? c.value : next[0].value;
          });
        }

        if (
          response.code === 'PAYMENT_METHOD_REQUIRED' &&
          typeof window !== 'undefined' &&
          typeof window.showToast === 'function'
        ) {
          window.showToast(
            errorMessage || 'Please choose a valid payment method.',
            'warning',
            9000
          );
        }

        if (validationErrors && Array.isArray(validationErrors)) {
          const errorText = validationErrors.map(err => {
            const fieldName = err.path || err.field || 'field';
            const msg = err.msg || err.message || err.error || String(err);
            return `${fieldName}: ${msg}`;
          }).join('; ');
          setError(errorText ? `Validation errors: ${errorText}` : (errorMessage || 'Please check your order details and try again.'));
          validationErrors.forEach(error => {
            console.error('Validation Error:', error);
          });
        } else {
          setError(errorMessage);
        }
      }
    } catch (err) {
      console.error('Error placing order:', err);
      setError(err.message || 'Failed to place order. Please try again.');
    } finally {
      setIsPlacingOrder(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="checkout-page">
        <div className="container">
          <SiteLoading
            title="Loading Checkout"
            subtitle="Preparing your order..."
            icon="💳"
            variant="full"
          />
        </div>
      </div>
    );
  }

  // No cart or empty cart
  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="checkout-empty">
            <h2>Your cart is empty</h2>
            <p>
              Add items from the shop, or open <strong>Bag</strong> if you added products before signing in.
              If the server cart failed to load, try Bag then checkout again.
            </p>
            <div className="checkout-empty-actions">
              <button type="button" className="btn-primary" onClick={() => navigate('/bag')}>
                Go to Bag
              </button>
              <button type="button" className="back-to-bag-btn" onClick={() => navigate('/')}>
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-header">
          <h1>Checkout</h1>
          <button className="back-to-bag-btn" onClick={() => navigate('/bag')}>
            ← Back to Bag
          </button>
        </div>

        {error && (
          <div className={`error-alert ${isExpiredCouponError ? 'error-alert--coupon-expired' : ''}`}>
            <i className="fas fa-exclamation-circle"></i>
            <span>{displayError}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="checkout-content">
          {/* Left Column - Order Details */}
          <div className="checkout-left">
            {/* Order Items */}
            <div className="checkout-section">
              <h2 className="section-title">Order Items</h2>
              <div className="order-items">
                {cart.items.map((item) => {
                  const productImage = item.product?.images || item.product?.image;
                  const imageUrl = getImageUrl(productImage);
                  
                  return (
                    <div key={item._id} className="order-item">
                      <div className="order-item-image">
                        <img 
                          src={imageUrl || PLACEHOLDER_NO_IMAGE_SMALL} 
                          alt={item.product.name}
                          onError={(e) => {
                            if (e.target.src !== PLACEHOLDER_NO_IMAGE_SMALL) {
                              e.target.src = PLACEHOLDER_NO_IMAGE_SMALL;
                            }
                          }}
                          loading="lazy"
                        />
                      </div>

                      <div className="order-item-content">
                        <h3 className="item-name">{item.product.name}</h3>

                        <div className="item-specs">
                          {item.color && (
                            <span className="spec">{item.color} Colour</span>
                          )}
                          {item.size && (
                            <span className="spec">Size: {item.size}</span>
                          )}
                        </div>

                        <div className="item-price">
                          {item.product.originalPrice && item.product.originalPrice > item.price ? (
                            <>
                              <span className="current-price">PKR {item.price.toLocaleString()}</span>
                              <span className="original-price">PKR {item.product.originalPrice.toLocaleString()}</span>
                            </>
                          ) : (
                            <span>PKR {item.price.toLocaleString()}</span>
                          )}
                        </div>

                        <div className="item-controls">
                          <div className="quantity-display">
                            <span className="quantity-label">Quantity:</span>
                            <span className="quantity-value">{item.quantity}</span>
                          </div>
                          <div className="item-total">
                            <span className="total-label">Total:</span>
                            <span className="total-value">PKR {(item.price * item.quantity).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Delivery Address — saved addresses (guest signup + delivery is on Bag) */}
            <div className="checkout-section">
              <h2 className="section-title">Delivery Address</h2>
              {userAddresses.length > 0 ? (
                <div className="address-selection">
                  {userAddresses.map((address) => (
                    <label 
                      key={address.id} 
                      className={`address-option ${selectedAddress?.id === address.id ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="deliveryAddress"
                        value={address.id}
                        checked={selectedAddress?.id === address.id}
                        onChange={() => setSelectedAddress(address)}
                      />
                      <div className="address-content">
                        <div className="address-title">
                          <strong>{address.title}</strong>
                          {address.isDefault && <span className="default-badge">Default</span>}
                        </div>
                        <div className="address-details">
                          <div>{address.name}</div>
                          <div>{address.address}</div>
                          <div>{address.city}</div>
                          <div>Phone: {address.phone}</div>
                        </div>
                      </div>
                      {selectedAddress?.id === address.id && (
                        <div className="address-checkmark">✓</div>
                      )}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="no-addresses">
                  <p>No delivery addresses found</p>
                  <button 
                    type="button"
                    className="add-address-btn"
                    onClick={() => navigate('/profile')}
                  >
                    + Add Delivery Address
                  </button>
                </div>
              )}
            </div>

            {/* Payment Method */}
            {isAuthenticated && (
            <div className="checkout-section" ref={paymentSectionRef}>
              <h2 className="section-title">
                Payment Method
              </h2>

              <div
                className="checkout-canonical-checkpoint"
                role="region"
                aria-label="Payment type for your order"
              >
                <h3 className="checkout-canonical-title">Choose payment type</h3>
                <p className="checkout-canonical-hint">
                  Select how you will pay. This is sent with your order.
                  {checkoutCanonicalApiMessage ? ` ${checkoutCanonicalApiMessage}` : ''}
                </p>
                {checkoutCanonicalLoading && checkoutCanonicalMethods.length === 0 ? (
                  <div className="payment-loading checkout-canonical-loading">
                    <i className="fas fa-spinner fa-spin" aria-hidden />
                    <span> Loading payment options…</span>
                  </div>
                ) : checkoutCanonicalMethodsVisible.length > 0 ? (
                  <div
                    className="checkout-canonical-grid"
                    role="radiogroup"
                    aria-label="Payment type options"
                  >
                    {checkoutCanonicalMethodsVisible.map((row) => (
                      <label
                        key={row.value}
                        className={`checkout-canonical-card ${
                          selectedCanonicalPaymentMethod === row.value ? 'selected' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="canonicalPaymentType"
                          value={row.value}
                          checked={selectedCanonicalPaymentMethod === row.value}
                          onChange={() => {
                            setSelectedCanonicalPaymentMethod(row.value);
                            setError(null);
                            setExpandedPaymentMethod(null);
                          }}
                        />
                        <div className="checkout-canonical-card-body">
                          <span className="checkout-canonical-card-label">{row.label}</span>
                          {row.description ? (
                            <span className="checkout-canonical-card-desc">{row.description}</span>
                          ) : null}
                        </div>
                      </label>
                    ))}
                  </div>
                ) : !checkoutCanonicalLoading &&
                  checkoutCanonicalMethods.length > 0 &&
                  checkoutCanonicalMethodsVisible.length === 0 ? (
                  <p className="checkout-canonical-fallback">
                    The payment types from the server do not match any active payment account in the
                    store. Add matching accounts in admin or contact support.
                  </p>
                ) : (
                  <p className="checkout-canonical-fallback">
                    Payment types could not be loaded from the server. Try again later or contact
                    support.
                  </p>
                )}
              </div>

              
              {paymentMethodsLoading ? (
                <div className="payment-loading" style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#6c757d'
                }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginRight: '10px' }}></i>
                  Loading payment methods...
                        </div>
              ) : activeStorefrontPaymentMethods.length === 0 ? (
                <div className="payment-error" style={{
                  padding: '20px',
                  background: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '8px',
                  color: '#856404'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: '10px' }}></i>
                  No payment methods available. Please contact support.
                    </div>
              ) : storefrontMethodsForSelectedCanonical.length === 0 ? (
                <div
                  className="checkout-payment-type-empty"
                  style={{
                    padding: '16px 18px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    color: '#555',
                    lineHeight: 1.5,
                  }}
                >
                  {selectedCanonicalPaymentMethod === 'cod' ? (
                    <p style={{ margin: 0 }}>
                      <strong>Cash on delivery</strong> — you will pay when your order arrives.
                    </p>
                  ) : (
                    <p style={{ margin: 0 }}>
                      No payment account is configured for this option. Choose another payment type
                      above or contact support.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {!selectedPaymentMethod && (
                    <div className="validation-message payment-validation">
                      <i className="fas fa-exclamation-circle"></i>
                      Please select a payment method to continue
                    </div>
                  )}
                  
                  <div className="payment-methods">
                    {storefrontMethodsForSelectedCanonical.map((method) => {
                      const isSelected = selectedPaymentMethod === method.type;
                      const hasDetails = paymentMethodHasDetails(method);
                      const isManuallyCollapsed = expandedPaymentMethod === 'collapsed-' + method.id;
                      const isExpanded = isSelected && hasDetails && !isManuallyCollapsed ? true : (expandedPaymentMethod === method.id);
                      
                      return (
                        <div 
                          key={method.id} 
                          className={`payment-option-wrapper ${isSelected ? 'selected' : ''} ${!selectedPaymentMethod ? 'required' : ''}`}
                        >
                          <label 
                            className={`payment-option ${isSelected ? 'selected' : ''} ${!selectedPaymentMethod ? 'required' : ''}`}
                            onClick={() => {
                              if (!isSelected) {
                                setSelectedPaymentMethod(method.type);
                                setError(null);
                                // Auto-expand when selected if has details
                                if (hasDetails) {
                                  setExpandedPaymentMethod(method.id);
                                } else {
                                  setExpandedPaymentMethod(null);
                                }
                              } else if (hasDetails) {
                                // If clicking on already selected method, toggle expansion
                                if (isExpanded) {
                                  setExpandedPaymentMethod('collapsed-' + method.id);
                                } else {
                                  setExpandedPaymentMethod(method.id);
                                }
                              }
                            }}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={method.type}
                              checked={isSelected}
                              onChange={(e) => {
                                setSelectedPaymentMethod(e.target.value);
                                setError(null);
                                // Auto-expand when selected if has details, clear any collapsed state
                                if (hasDetails) {
                                  setExpandedPaymentMethod(method.id);
                                } else {
                                  setExpandedPaymentMethod(null);
                                }
                              }}
                              required
                          />
                          <div className="payment-content">
                            <i className={method.icon}></i>
                              <div className="payment-info">
                                <span className="payment-name">{method.name}</span>
                                {hasDetails && (
                                  <button
                                    type="button"
                                    className="payment-toggle-details"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      // Toggle expansion: if selected and expanded, collapse; otherwise expand
                                      if (isSelected && isExpanded) {
                                        // Collapse selected method
                                        setExpandedPaymentMethod('collapsed-' + method.id);
                                      } else {
                                        // Expand this method
                                        setExpandedPaymentMethod(method.id);
                                      }
                                    }}
                                  >
                                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                  </button>
                                )}
                          </div>
                            </div>
                        </label>
                          
                          {/* Expandable Details Section */}
                          {isExpanded && hasDetails && (
                            <div className="payment-details-expanded">
                              {method.description && (
                                <div className="payment-detail-item">
                                  <i className="fas fa-info-circle"></i>
                                  <span className="payment-detail-label">Description:</span>
                                  <span className="payment-detail-value">{method.description}</span>
                  </div>
                )}
                              {(() => {
                                const accountNum = String(method.accountNumber || '')
                                  .replace(/\u00a0/g, ' ')
                                  .trim();
                                if (accountNum && accountNum !== '') {
                                  const digitCount = accountNum.replace(/\D/g, '').length;
                                  const looksPartial = digitCount > 0 && digitCount <= 4;
                                  return (
                                    <div className="payment-detail-item payment-detail-item--account">
                                      <i className="fas fa-hashtag"></i>
                                      <div className="payment-account-block">
                                        <div className="payment-account-head">
                                          <span className="payment-detail-label payment-detail-label--account">
                                            Account number
                                          </span>
                                          <button
                                            type="button"
                                            className={`payment-copy-account-btn${copiedPaymentMethodId === method.id ? ' copied' : ''}`}
                                            title="Copy full account number"
                                            aria-label="Copy full account number"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              copyPaymentAccountNumber(accountNum, method.id);
                                            }}
                                          >
                                            <i
                                              className={
                                                copiedPaymentMethodId === method.id
                                                  ? 'fas fa-check'
                                                  : 'fas fa-copy'
                                              }
                                              aria-hidden
                                            />
                                            {copiedPaymentMethodId === method.id ? ' Copied' : ' Copy'}
                                          </button>
                                        </div>
                                        <div className="payment-account-number-wrap">
                                          <span className="payment-account-number" title={accountNum}>
                                            {accountNum}
                                          </span>
                                        </div>
                                        {looksPartial && (
                                          <span className="payment-masked-note">
                                            If this looks incomplete, contact the store for full bank details.
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              {method.accountTitle && (
                                <div className="payment-detail-item">
                                  <i className="fas fa-user"></i>
                                  <span className="payment-detail-label">Account Title:</span>
                                  <span className="payment-detail-value">{method.accountTitle}</span>
                                </div>
                              )}
                              {method.bankName && (
                                <div className="payment-detail-item">
                                  <i className="fas fa-university"></i>
                                  <span className="payment-detail-label">Bank Name:</span>
                                  <span className="payment-detail-value">{method.bankName}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            )}

            {/* Promo Code */}
            <div className="checkout-section">
              <h2 className="section-title">Promo Code</h2>
              <div className="promo-section">
                {savedCouponCodes.length > 0 && (
                  <div className="checkout-saved-coupons">
                    <p className="checkout-saved-coupons__label">Your saved codes</p>
                    <p className="checkout-saved-coupons__hint">
                      Coupons are not applied until you tap Apply.
                    </p>
                    <div className="checkout-saved-coupons__chips">
                      {savedCouponCodes.map((code) => (
                        <div key={code} className="checkout-saved-coupon-chip">
                          <span className="checkout-saved-coupon-chip__code">{code}</span>
                          <button
                            type="button"
                            className="checkout-saved-coupon-chip__apply"
                            disabled={applyingCoupon}
                            onClick={() => applyPromoCode(code)}
                          >
                            Apply
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {couponNotice?.text && (
                  <div
                    className={`coupon-notice ${
                      couponNotice.type === 'expired' ? 'coupon-notice--gold' : ''
                    } ${couponNotice.type === 'success' ? 'coupon-notice--success' : ''}`}
                    role="alert"
                  >
                    {couponNotice.text}
                  </div>
                )}
                {cart.coupon ? (
                  <div className="applied-coupon">
                    <div className="coupon-info">
                      <i className="fas fa-ticket-alt"></i>
                      <span><strong>{cart.coupon.code}</strong> applied</span>
                      <span className="coupon-discount">-PKR {couponDiscount.toLocaleString()}</span>
                    </div>
                    <button 
                      className="remove-coupon-btn"
                      onClick={handleRemoveCoupon}
                      disabled={applyingCoupon}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="promo-input">
                    <input
                      type="text"
                      placeholder="Enter promo code"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value);
                        if (couponNotice?.text) setCouponNotice(null);
                      }}
                      className="promo-field"
                      disabled={applyingCoupon}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleApplyCoupon();
                        }
                      }}
                    />
                    <button 
                      className="apply-promo-btn"
                      onClick={handleApplyCoupon}
                      disabled={applyingCoupon || !promoCode.trim()}
                    >
                      {applyingCoupon ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
                <div className="promo-extras">
                  <button
                    type="button"
                    className="promo-codes-link"
                    onClick={() =>
                      navigate('/profile', { state: { section: 'coupons' } })
                    }
                  >
                    <i className="fas fa-ticket-alt" />
                    COUPONS
                  </button>
                  {waLinks.showOrderBtn && waLinks.orderUrl && !isAuthenticated && (
                    <a
                      href={waLinks.orderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="promo-wa-order-btn"
                    >
                      <i className="fab fa-whatsapp" />
                      Order via WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="checkout-right">
            <div className="order-summary-card">
              <h2 className="summary-title">Order Summary</h2>
              
              <div className="summary-content">
                {/* 1. Total Amount - First (Original amount before discount) */}
                <div className="summary-total">
                  <span>Total Amount</span>
                  <span>PKR {totalOriginalBeforeDiscount.toLocaleString()}</span>
                </div>

                {/* 2. Discount - Second - Always show if there's any discount */}
                    {saleDiscount > 0 && (
                      <div className="summary-line discount">
                        <span>
                          <i className="fas fa-tag"></i> Sale Discount {saleDiscountPercent > 0 && `(${saleDiscountPercent}% OFF)`}
                        </span>
                        <span className="summary-value">-PKR {saleDiscount.toLocaleString()}</span>
                      </div>
                )}

                {couponDiscount > 0 && (
                  <div className="summary-line discount">
                    <span>
                      <i className="fas fa-ticket-alt"></i> Coupon Discount
                    </span>
                    <span className="summary-value">-PKR {couponDiscount.toLocaleString()}</span>
                  </div>
                )}
                {cart?.coupon && couponTerms.length > 0 && (
                  <div className="summary-coupon-terms" role="note" aria-label="Coupon terms">
                    <div className="summary-coupon-terms__title">
                      {cart.coupon?.code ? `Coupon ${cart.coupon.code} terms` : 'Coupon terms'}
                    </div>
                    <div className="summary-coupon-terms__list">
                      {couponTerms.map((term, idx) => (
                        <span key={`${term}-${idx}`}>{term}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 3. Shipping - Third */}
                <div className="summary-line summary-shipping">
                  <span>
                    <i className="fas fa-truck"></i> Shipping
                  </span>
                  <span className="summary-value">
                    {shipping === 0 ? (
                      <span className="free-shipping">FREE</span>
                    ) : (
                      `PKR ${shipping.toLocaleString()}`
                    )}
                  </span>
                </div>

                <div className="summary-divider"></div>

                {/* 4. Final total (after coupon + shipping) */}
                <div className="summary-line summary-subtotal">
                  <span>Total (incl. shipping)</span>
                  <span className="summary-value">PKR {total.toLocaleString()}</span>
                </div>
              </div>

              <div className="checkout-actions">
                {selectedPaymentMethod &&
                  selectedPaymentMethod !== 'cash-on-delivery' && (
                    <div className="checkout-payment-proof-panel">
                      <div className="checkout-payment-proof-panel__head">
                        <span
                          className="checkout-payment-proof-panel__icon"
                          aria-hidden
                        >
                          📱
                        </span>
                        <div>
                          <h3 className="checkout-payment-proof-panel__title">
                            Send your payment proof on WhatsApp
                          </h3>
                          <p className="checkout-payment-proof-panel__lead">
                            After you transfer the amount using the account details
                            above, tap the button below and send us a{' '}
                            <strong>screenshot or receipt</strong> on WhatsApp so we
                            can verify your payment before we process your order.
                          </p>
                          {paymentProofTargetDigits && (
                            <p className="checkout-payment-proof-panel__dest">
                              {paymentProofUsesAccountNumber ? (
                                <>
                                  WhatsApp will open to:{' '}
                                  <strong>{paymentProofTargetDigits}</strong>
                                  <span className="checkout-payment-proof-panel__dest-note">
                                    {' '}
                                    (number from this payment account)
                                  </span>
                                </>
                              ) : (
                                <>
                                  WhatsApp will open to:{' '}
                                  <strong>{paymentProofTargetDigits}</strong>
                                  <span className="checkout-payment-proof-panel__dest-note">
                                    {' '}
                                    (store default — set &quot;Payment proof —
                                    WhatsApp&quot; on this account in Admin → Revenue
                                    to use a dedicated number)
                                  </span>
                                </>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                      {paymentProofWhatsAppUrl ? (
                        <a
                          href={paymentProofWhatsAppUrl}
                          className="checkout-payment-proof-wa"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fab fa-whatsapp" aria-hidden />
                          Open WhatsApp — send payment proof
                        </a>
                      ) : (
                        <p className="checkout-payment-proof-panel__warn">
                          WhatsApp is not configured for this store. Please contact
                          support with your payment proof if needed.
                        </p>
                      )}
                      <label className="checkout-payment-proof-checkbox">
                        <input
                          type="checkbox"
                          checked={paymentTransferConfirmed}
                          onChange={(e) =>
                            setPaymentTransferConfirmed(e.target.checked)
                          }
                        />
                        <span>
                          I confirm that I have completed the payment successfully
                        </span>
                      </label>
                    </div>
                  )}

                <button 
                  className={`place-order-btn ${isPlacingOrder ? 'processing' : ''}`}
                  onClick={handlePlaceOrder}
                  disabled={
                    isPlacingOrder ||
                    !selectedAddress ||
                    !selectedPaymentMethod ||
                    (selectedPaymentMethod !== 'cash-on-delivery' &&
                      !paymentTransferConfirmed)
                  }
                >
                  {isPlacingOrder ? (
                    <>
                      <div className="btn-spinner"></div>
                      Processing...
                    </>
                  ) : (
                    'Place Order'
                  )}
                </button>

                {checkoutWhatsAppOrderUrl && !isAuthenticated && (
                  <a
                    href={checkoutWhatsAppOrderUrl}
                    className="checkout-wa-order-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-whatsapp" aria-hidden />
                    Order via WhatsApp
                  </a>
                )}

                <div className="shipping-info">
                  {deliveryInfo.freeDeliveryEnabled ? (
                    <>
                      <p>
                        📦 Free delivery on orders PKR{' '}
                        {deliveryInfo.threshold.toLocaleString()} or more (otherwise standard shipping
                        PKR {deliveryInfo.standardShippingFee.toLocaleString()}).
                      </p>
                      {!deliveryInfo.qualified && deliveryInfo.amountToAdd > 0 && (
                        <p className="free-delivery-hint">
                          Add PKR {deliveryInfo.amountToAdd.toLocaleString()} more to unlock free
                          delivery on this order.
                        </p>
                      )}
                      {deliveryInfo.qualified && (
                        <p className="free-delivery-hint free-delivery-qualified">
                          You qualify for free delivery on this order.
                        </p>
                      )}
                    </>
                  ) : (
                    <p>
                      📦 Standard shipping: PKR {deliveryInfo.standardShippingFee.toLocaleString()}
                    </p>
                  )}
                  <p>🚚 Delivery in 2-3 business days</p>
                  <p>💯 100% authentic products</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default CheckoutPage;

